/**
 * @fileoverview ProtectedRoute — authentication + RBAC route guard
 *
 * Usage:
 *   <ProtectedRoute>                          — require auth only
 *   <ProtectedRoute permission="finance.view"> — require auth + permission
 *   <ProtectedRoute anyOf={["finance.view","finance.admin"]}>
 */

import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { useAuth } from "context/AuthContext";

function AccessDenied() {
  return (
    <SoftBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      textAlign="center"
      px={3}
    >
      <Icon sx={{ fontSize: "4rem !important", color: "error.main", mb: 2 }}>lock</Icon>
      <SoftTypography variant="h4" fontWeight="bold" color="dark" mb={1}>
        Access Denied
      </SoftTypography>
      <SoftTypography variant="body2" color="text" mb={3} maxWidth={400}>
        You don&apos;t have permission to access this section. Contact your system administrator
        if you believe this is an error.
      </SoftTypography>
      <SoftButton
        variant="gradient"
        color="info"
        component="a"
        href="/dashboards/executive"
      >
        <Icon sx={{ mr: 0.5 }}>home</Icon>
        Back to Dashboard
      </SoftButton>
    </SoftBox>
  );
}

export function ProtectedRoute({ children, permission, anyOf }) {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  // Still resolving stored token — render nothing to avoid flash
  if (loading) return null;

  // Not logged in → redirect to sign-in, preserving intended destination
  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }

  // Logged in but lacks required permission
  if (permission && !hasPermission(permission)) return <AccessDenied />;
  if (anyOf && !hasAnyPermission(anyOf)) return <AccessDenied />;

  return children;
}

ProtectedRoute.defaultProps = {
  permission: null,
  anyOf: null,
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
  anyOf: PropTypes.arrayOf(PropTypes.string),
};
